import * as THREE from 'three/webgpu';
import {
	Fn,
	exp,
	float,
	hash,
	mix,
	mrt,
	output,
	pass,
	perspectiveDepthToViewZ,
	renderOutput,
	screenCoordinate,
	screenUV,
	smoothstep,
	uniform,
	vec3,
	vec4
} from 'three/tsl';
import { CloudTemporalResolveNode } from './CloudTemporalResolveNode.js';
import { CloudSpatialUpscaleNode } from './CloudSpatialUpscaleNode.js';

function createCompositeNode( sceneColor, sceneDepth, cloudColor, cloudData, uniforms ) {

	return Fn( () => {

		const resolvedCloud = cloudColor.sample( screenUV ).toVar();
		const resolvedData = cloudData.sample( screenUV );
		resolvedCloud.a.assign( resolvedData.b.clamp() );
		const cloudFrontDistance = resolvedData.r;
		const cloudMedianDistance = resolvedData.g;
		const opaqueDepth = sceneDepth.sample( screenUV ).r;
		const opaqueDistance = perspectiveDepthToViewZ( opaqueDepth, uniforms.cameraNear, uniforms.cameraFar ).negate();
		const depthVisibility = smoothstep(
			uniforms.occlusionSoftness.negate(),
			uniforms.occlusionSoftness,
			opaqueDistance.sub( cloudFrontDistance )
		);
		resolvedCloud.mulAssign( depthVisibility );

		const background = sceneColor.sample( screenUV );
		const opaquePresence = smoothstep( 0.997, 0.9999, opaqueDepth ).oneMinus();
		const opaqueFog = float( 1 ).sub( exp( opaqueDistance.mul( uniforms.aerialDensity ).negate() ) )
			.mul( opaquePresence ).mul( uniforms.aerialEnabled );
		const cloudFog = float( 1 ).sub( exp( cloudMedianDistance.mul( uniforms.aerialDensity ).negate() ) )
			.mul( uniforms.aerialEnabled );
		const atmosphereColor = vec3( uniforms.aerialColor );
		const backgroundRadiance = mix( background.rgb, atmosphereColor, opaqueFog );
		const cloudRadiance = resolvedCloud.rgb.mul( cloudFog.oneMinus() )
			.add( atmosphereColor.mul( resolvedCloud.a ).mul( cloudFog ) );
		const composite = cloudRadiance.add( backgroundRadiance.mul( resolvedCloud.a.oneMinus() ) );

		return vec4( composite, 1 );

	} )();

}

function createDisplayDitherNode( linearColor, renderer ) {

	const displayColor = renderOutput( linearColor, renderer.toneMapping, renderer.outputColorSpace );

	return Fn( () => {

		const color = displayColor.toVar();
		const pixelSeed = screenCoordinate.x.add( screenCoordinate.y.mul( 8192 ) );
		// Dither after tone mapping and sRGB conversion, immediately before the
		// 8-bit display target. Each channel receives a decorrelated sub-code-value
		// offset, which breaks visible gradient contours without softening detail.
		const dither = vec3(
			hash( pixelSeed ),
			hash( pixelSeed.add( 104729 ) ),
			hash( pixelSeed.add( 224737 ) )
		).sub( 0.5 ).div( 255 );

		return vec4( color.rgb.add( dither ).clamp(), color.a );

	} )();

}

/**
 * Full-resolution scene rendering plus a lower-resolution cloud pass with
 * depth/transmittance-aware reconstruction and premultiplied composition.
 */
export class WebGPUCloudRenderPipeline {

	constructor( renderer, options = {} ) {

		if ( ! renderer?.isWebGPURenderer ) {

			throw new TypeError( 'WebGPUCloudRenderPipeline requires a Three.js WebGPURenderer.' );

		}

		if ( ! options.scene?.isScene || ! options.cloudScene?.isScene || ! options.camera?.isCamera ) {

			throw new TypeError( 'WebGPUCloudRenderPipeline requires scene, cloudScene, and camera options.' );

		}

		this.renderer = renderer;
		this.scene = options.scene;
		this.cloudScene = options.cloudScene;
		this.camera = options.camera;
		this.cloudVolume = options.cloudVolume ?? null;
		this._temporalMotion = new THREE.Vector3();
		this.uniforms = {
			depthSharpness: uniform( options.depthSharpness ?? 0.08 ),
			alphaSharpness: uniform( options.alphaSharpness ?? 10 ),
			occlusionSoftness: uniform( options.occlusionSoftness ?? 3 ),
			aerialColor: uniform( new THREE.Color( options.aerialColor ?? 0x9bb7c8 ) ),
			aerialDensity: uniform( options.aerialDensity ?? 0 ),
			aerialEnabled: uniform( options.aerialDensity > 0 ? 1 : 0 ),
			cameraNear: uniform( this.camera.near ),
			cameraFar: uniform( this.camera.far )
		};

		this.scenePass = pass( this.scene, this.camera, { samples: options.sceneSamples ?? 4 } );
		this.cloudPass = pass( this.cloudScene, this.camera, { samples: 0, depthBuffer: false } );
		this.cloudPass.setResolutionScale( options.cloudScale ?? 0.72 );
		this.cloudPass.setMRT( mrt( { output } ) );

		const sceneColor = this.scenePass.getTextureNode( 'output' );
		const sceneDepth = this.scenePass.getTextureNode( 'depth' );
		const cloudColor = this.cloudPass.getTextureNode( 'output' );
		const cloudData = this.cloudPass.getTextureNode( 'cloudData' );

		this.scenePass.getTexture( 'output' ).colorSpace = THREE.LinearSRGBColorSpace;
		this.cloudPass.getTexture( 'output' ).colorSpace = THREE.LinearSRGBColorSpace;
		this.cloudPass.getTexture( 'cloudData' ).colorSpace = THREE.NoColorSpace;
		// The spatial pass needs filterable opacity for cubic coverage
		// reconstruction. Depth is sampled exactly at texel centers there.
		this.cloudPass.getTexture( 'cloudData' ).minFilter = THREE.LinearFilter;
		this.cloudPass.getTexture( 'cloudData' ).magFilter = THREE.LinearFilter;
		this.spatialUpscale = new CloudSpatialUpscaleNode( cloudColor, cloudData );
		const spatialCloudColor = this.spatialUpscale.getTextureNode();
		const spatialCloudData = this.spatialUpscale.getDataTextureNode();
		this.temporal = new CloudTemporalResolveNode( spatialCloudColor, spatialCloudData, this.camera, {
			enabled: options.temporalEnabled,
			historyWeight: options.historyWeight,
			depthThreshold: options.temporalDepthThreshold,
			alphaThreshold: options.temporalAlphaThreshold,
			motionRejection: options.motionRejection,
			depthSharpness: this.uniforms.depthSharpness.value,
			alphaSharpness: this.uniforms.alphaSharpness.value,
			clampExpansion: options.clampExpansion,
			detailBlur: options.detailBlur
		} );
		const resolvedCloudColor = this.temporal.getTextureNode();
		const resolvedCloudData = this.temporal.getDataTextureNode();
		this.pipeline = new THREE.RenderPipeline( renderer );
		const composite = createCompositeNode( sceneColor, sceneDepth, resolvedCloudColor, resolvedCloudData, this.uniforms );
		this.pipeline.outputColorTransform = false;
		this.pipeline.outputNode = createDisplayDitherNode( composite, renderer );

	}

	setCloudScale( scale ) {

		const nextScale = Math.min( 1, Math.max( 0.1, scale ) );
		if ( nextScale !== this.cloudPass.getResolutionScale() ) this.temporal.reset();
		this.cloudPass.setResolutionScale( nextScale );
		return this;

	}

	setReconstruction( patch = {} ) {

		this.temporal.setOptions( {
			depthSharpness: patch.depthSharpness,
			alphaSharpness: patch.alphaSharpness
		} );
		if ( Number.isFinite( patch.depthSharpness ) ) this.uniforms.depthSharpness.value = this.temporal.options.depthSharpness;
		if ( Number.isFinite( patch.alphaSharpness ) ) this.uniforms.alphaSharpness.value = this.temporal.options.alphaSharpness;
		if ( Number.isFinite( patch.occlusionSoftness ) ) {

			this.uniforms.occlusionSoftness.value = Math.min( 50, Math.max( 0.01, patch.occlusionSoftness ) );

		}

		return this;

	}

	setTemporalOptions( patch = {} ) {

		this.temporal.setOptions( patch );
		if ( Number.isFinite( patch.depthSharpness ) ) this.uniforms.depthSharpness.value = this.temporal.options.depthSharpness;
		if ( Number.isFinite( patch.alphaSharpness ) ) this.uniforms.alphaSharpness.value = this.temporal.options.alphaSharpness;
		return this;

	}

	getTemporalOptions() {

		return {
			...this.temporal.options,
			depthSharpness: this.uniforms.depthSharpness.value,
			alphaSharpness: this.uniforms.alphaSharpness.value,
			occlusionSoftness: this.uniforms.occlusionSoftness.value
		};

	}

	setTemporalMotion( offset ) {

		this.temporal.setMotionOffset( offset );
		return this;

	}

	resetTemporal() {

		this.temporal.reset();
		return this;

	}

	setAerialPerspective( patch = {} ) {

		if ( patch.color?.isColor ) this.uniforms.aerialColor.value.copy( patch.color );
		else if ( patch.color !== undefined ) this.uniforms.aerialColor.value.set( patch.color );
		if ( Number.isFinite( patch.density ) ) this.uniforms.aerialDensity.value = Math.max( 0, patch.density );
		if ( patch.enabled !== undefined ) this.uniforms.aerialEnabled.value = patch.enabled ? 1 : 0;
		return this;

	}

	render() {

		this.uniforms.cameraNear.value = this.camera.near;
		this.uniforms.cameraFar.value = this.camera.far;
		if ( this.cloudVolume?.getWorldAdvectionOffset ) {

			this.cloudVolume.getWorldAdvectionOffset( this._temporalMotion );
			this.temporal.setMotionOffset( this._temporalMotion );
			const parameters = this.cloudVolume.parameters;
			const deformationRate = parameters.formationStrength * parameters.formationSpeed
				+ parameters.roilStrength * parameters.roilSpeed * 0.35;
			this.temporal.setDeformationRate( deformationRate );

		}
		this.pipeline.render();

	}

	getStats() {

		return {
			cloudScale: this.cloudPass.getResolutionScale(),
			cloudWidth: this.cloudPass.renderTarget.width,
			cloudHeight: this.cloudPass.renderTarget.height,
			sceneWidth: this.scenePass.renderTarget.width,
			sceneHeight: this.scenePass.renderTarget.height,
			spatialUpscale: 'cubic-b-spline',
			temporalEnabled: this.temporal.options.enabled,
			historyWeight: this.temporal.options.historyWeight,
			temporalDepthThreshold: this.temporal.options.depthThreshold,
			motionRejection: this.temporal.options.motionRejection
		};

	}

	dispose() {

		this.scenePass.dispose();
		this.cloudPass.dispose();
		this.spatialUpscale.dispose();
		this.temporal.dispose();
		this.pipeline.dispose();

	}

}
